import { ChevronDown } from "lucide-react";
import { IChartData } from "@/interfaces/dashboard.interface";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Skeleton } from "../ui/skeleton";

interface RevenueChartCardProps {
  title: string;
  value: string;
  percentage: number;
  period: string;
  data: IChartData[];
  isLoading?: boolean;
}

export default function RevenueChartCard({
  title,
  value,
  percentage,
  period,
  data,
  isLoading
}: RevenueChartCardProps) {
  const isPositive = percentage >= 0;

  return (
    <div className="bg-[#15203C] rounded-[20px] p-[30px] w-full h-[467px] flex flex-col">
      {isLoading ? (
        <Skeleton className="h-full" />
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between mb-[10px]">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-[#F6F6F6]">{title}</h3>
              <div className="bg-[#0C1427] rounded-[20px] px-[10px] py-1">
                <p className={`text-xs ${isPositive ? 'text-[#13D480]' : 'text-[#DC4426]'}`}>
                  {isPositive ? '+' : ''}{percentage}%
                </p>
              </div>
            </div>
            <button className="flex items-center gap-1 text-xs text-[#888888] hover:text-white transition-colors">
              <span>{period}</span>
              <ChevronDown className="size-4" />
            </button>
          </div>

          {/* Value + Legend */}
          <div className="flex items-center justify-between mb-[19px]">
            <p className="text-xl font-bold text-white">{value}</p>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-[10px]">
                <div className="size-[7px] rounded-full bg-[#7846FF]" />
                <p className="text-xs text-white">Completos</p>
              </div>
              <div className="flex items-center gap-[10px]">
                <div className="size-[7px] rounded-full bg-[#45169C]" />
                <p className="text-xs text-white">Pendentes</p>
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 min-h-[200px]">
            {data.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                Sem dados disponíveis
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={data} barGap={5} barCategoryGap={32} margin={{ left: 0, right: 0, top: 10, bottom: 10 }}>
                  <YAxis
                    stroke="#888888"
                    tick={{ fill: '#888888', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    domain={[0, 700]}
                    ticks={[100, 200, 300, 400, 500, 600, 700]}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#888888"
                    tick={{ fill: '#888888', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#15203C",
                      border: "1px solid #5D5D5D",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#F6F6F6" }}
                    itemStyle={{ color: "#F6F6F6" }}
                  />
                  <Bar dataKey="value1" fill="#7846FF" radius={[4, 4, 0, 0]} barSize={49} />
                  <Bar dataKey="value2" fill="#45169C" radius={[4, 4, 0, 0]} barSize={49} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
